import { useContext, useEffect, useRef, useState } from "react";
import {
  Cartesian3,
  Entity,
  GeoJsonDataSource,
  Ion,
  KmlDataSource,
  viewerDragDropMixin,
  createGooglePhotorealistic3DTileset,
  IonGeocodeProviderType,
  GoogleMaps,
  Math as CesiumMath,
  Plane,
  ImageMaterialProperty,
  Transforms,
  HeadingPitchRoll,
  Terrain,
  CallbackProperty,
  CallbackPositionProperty,
  Cartographic,
  PropertyBag,
  sampleTerrainMostDetailed,
} from "cesium";
import { environment } from "../../env";
import { CesiumComponentRef, Viewer } from "resium";
import { Viewer as CesiumViewer } from "cesium";
import {
  AreaMeasure,
  DistanceMeasure,
  DistanceSurfaceMeasure,
} from "@cesium-extends/measure";
import { MeasurePanel, MeasureType } from "./measurePanel";
import { UnitConverterHelper } from "../../helpers/unitConverterHelper";
import Context from "../utils/context";
import { Themes } from "../../enums/themes";
import { FilesList } from "./filesList";
import { Utils } from "../../helpers/utils";
import shp from "shpjs";
import dxfParser from "dxf-parser";
import { IShapeFile } from "../../../components/earthtwin/models";
import {
  LocationBookmarksV1Service,
  QrCodesV1Service,
  ShapeFilesV1Service,
} from "../../services/api/api";
import { MapSettings } from "./mapSettings";
import QRCode from "qrcode";
import { v4 as uuidv4 } from "uuid";
import { IQrCode } from "../../../components/admin/models";
import { QRCodePanel } from "./QRCodePanel";
import _ from "lodash";
import { LocationBookmarkPanel } from "./bookmarkLocations/locationBookmarkPanel";
import { useLocation, useParams } from "react-router-dom";

const QR_SIZE = 1;
const METER_IN_RADIAN = 1 / 111319.9;
const QR_SPACE = METER_IN_RADIAN * 0.001;

export const ResiumMap = () => {
  Ion.defaultAccessToken = environment.Cesium_Default_Access_Token;
  GoogleMaps.defaultApiKey = environment.Google_Api_Key;

  const context = useContext(Context);
  const [shapeFiles, setShapeFiles] = useState<IShapeFile[]>([]);

  const [measurementEnded, SetMeasurementEnded] = useState<boolean>(false);
  const [distanceSurfaceMeasure, setDistanceSurfaceMeasure] =
    useState<DistanceSurfaceMeasure | null>(null);
  const [distanceMeasure, setDistanceMeasure] =
    useState<DistanceMeasure | null>(null);
  const [distanceValue, setDistanceValue] = useState<number>(0);
  const [areaMeasure, setAreaMeasure] = useState<AreaMeasure | null>(null);
  const [measurementEntity, setMeasurementEntity] = useState<Entity | null>(
    null
  );

  //const [measureType, setMeasureType] = useState<MeasureType | null>(null);
  const [measurementUnit, setMeasurementUnit] = useState<string>("");

  const [enable3DMap, setEnable3DMap] = useState<boolean>(false);
  const [isShowLables, setIsShowLables] = useState<boolean>(true);
  const [enableQRCode, setEnableQRCode] = useState<boolean>(false);
  const [currentQR, setCurrentQR] = useState<IQrCode | null>(null);
  const [allQR, setAllQR] = useState<IQrCode[] | null>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isQRSaving, setQRIsSaving] = useState(false);
  const [isMeasuring, setIsMeasuring] = useState(false);

  const ref = useRef<CesiumComponentRef<CesiumViewer>>(null);

  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isBookmark = location.pathname.includes("/bookmark/");

  const getCurrentPosition = async () => {
    if (ref.current && ref.current.cesiumElement) {
      if (
        context.currentPosition.longitude != 0 &&
        context.currentPosition.latitude != 0
      ) {
        ref.current.cesiumElement.camera.flyTo({
          destination: Cartesian3.fromDegrees(
            context.currentPosition.longitude,
            context.currentPosition.latitude,
            context.currentPosition.altitude || 12000
          ),
          duration: 4,
        });
      }
    }
  };

  useEffect(() => {
    initMap();

    if (isBookmark) {
      if (id) {
        goToLocationBookmarkById(id);
      }
    } else {
      getCurrentPosition();
      if (allQR == null || allQR.length == 0) {
        getAllQRCodes();
      }
    }
  }, [ref.current && ref.current.cesiumElement, id]);

  useEffect(() => {
    setShapeFiles(context.shapeFiles);
  }, [context.shapeFiles]);

  useEffect(() => {
    if (currentQR !== null) {
      updateQREntity(currentQR);
    }
  }, [currentQR]);

  useEffect(() => {
    if (ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;

      viewer.baseLayerPicker.viewModel.selectedImagery = isShowLables
        ? viewer.baseLayerPicker.viewModel.imageryProviderViewModels[1]
        : viewer.baseLayerPicker.viewModel.imageryProviderViewModels[0];
    }
  }, [ref.current?.cesiumElement, isShowLables]);

  const initMap = async () => {
    if (ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;

      viewer.scene.setTerrain(
        Terrain.fromWorldTerrain({
          requestWaterMask: true,
          requestVertexNormals: true,
        })
      );

      viewer.camera.changed.addEventListener(function () {
        const cartographic = viewer.camera.positionCartographic;
        context.setCurrentPosition(
          CesiumMath.toDegrees(cartographic.latitude),
          CesiumMath.toDegrees(cartographic.longitude),
          cartographic.height,
        );
      });
    }
  };

  const goToLocationBookmarkById = async (id: string) => {
    const LocationBookmarksService = new LocationBookmarksV1Service();
    const response = await LocationBookmarksService.get(id);
    const bookmark = response.data;
    if (bookmark) {
      const { latitude, longitude, altitude } = bookmark;
      if (ref.current && ref.current.cesiumElement) {
        ref.current.cesiumElement.camera.flyTo({
          destination: Cartesian3.fromDegrees(
            longitude,
            latitude,
            altitude || 12000
          ),
          duration: 4,
        });
      }
    }
  };

  const getAllQRCodes = async () => {
    const qrCodeService = new QrCodesV1Service();
    const allQRCodes = await qrCodeService.getQrCodes();

    if (allQRCodes.data !== null) {
      allQRCodes.data.forEach((qr) => {
        addQRCode(qr, false);
      });
    }

    setAllQR(allQRCodes.data);
  };

  const areaSurfaceMeasure = (measurementUnit: string) => {
    setMeasurementUnit(measurementUnit);
    setIsMeasuring(true);

    if (ref.current && ref.current.cesiumElement) {
      const areaMeasure = new AreaMeasure(ref.current.cesiumElement, {
        units: "millimeters",
        onEnd: (entity) => {
          SetMeasurementEnded(true);
          setMeasurementEntity(entity);
          const area = getAreaMeasure(entity, measurementUnit) ?? 0;
          setDistanceValue(area);
        },
      });
      areaMeasure.start();
      setAreaMeasure(areaMeasure);
    }
  };

  const nonGroundDistance = (measurementUnit: string) => {
    setMeasurementUnit(measurementUnit);
    setIsMeasuring(true);

    if (ref.current && ref.current.cesiumElement) {
      const distanceMeasure = new DistanceMeasure(ref.current.cesiumElement, {
        onEnd: (entity) => {
          SetMeasurementEnded(true);
          setMeasurementEntity(entity);
          const distance =
            GetDistanceMeasure(entity, measurementUnit, distanceMeasure) ?? 0;
          setDistanceValue(distance);
        },
        units: measurementUnit as any,
      });
      distanceMeasure.start();
      setDistanceMeasure(distanceMeasure);
    }
  };

  const groundDistanceMeasurement = (measurementUnit: string) => {
    setMeasurementUnit(measurementUnit);
    setIsMeasuring(true);

    if (ref.current && ref.current.cesiumElement) {
      const distanceSurfaceMeasure = new DistanceSurfaceMeasure(
        ref.current.cesiumElement,
        {
          onEnd: (entity) => {
            SetMeasurementEnded(true);
            setMeasurementEntity(entity);
            const distance =
              GetSurfaceMeasure(
                entity,
                measurementUnit,
                distanceSurfaceMeasure
              ) ?? 0;
            setDistanceValue(distance);
          },
          units: measurementUnit as any,
        }
      );
      distanceSurfaceMeasure.start();
      setDistanceSurfaceMeasure(distanceSurfaceMeasure);
    }
  };

  const saveMeasurement = async (name: string, measureType: MeasureType) => {
    setIsSaving(true);

    if (!measurementEntity) return;

    const distancePropertyName =
      measureType == MeasureType.Area ? "Area" : "Distance";
    let unit = measurementUnit;

    if (measureType == MeasureType.Area) {
      if (unit !== "acres" && unit !== "hectares") {
        unit += " squared";
      }
    }

    name = name.trim();
    const fileName = `${name}.geojson`;
    measurementEntity.name = name;

    const properties = {
      Name: name,
      Type: measureType,
      [distancePropertyName]: distanceValue.toFixed(5),
      Unit: unit,
    };
    const geojson = Utils.convertCesiumEntityToGeoJSON(
      measurementEntity,
      properties
    );

    const file = new File([JSON.stringify(geojson)], fileName, {
      type: "application/json",
    });

    await onFileUpload(file);

    cancelMeasurement();
    setIsSaving(false);
  };

  const cancelMeasurement = () => {
    setIsMeasuring(false);
    clearMeasurement();
  };

  const clearMeasurement = () => {
    if (distanceSurfaceMeasure) {
      distanceSurfaceMeasure.destroy();
      setDistanceSurfaceMeasure(null);
    }
    if (distanceMeasure) {
      distanceMeasure.destroy();
      setDistanceMeasure(null);
    }
    if (areaMeasure) {
      areaMeasure.destroy();
      setAreaMeasure(null);
    }

    SetMeasurementEnded(false);
  };

  const GetDistanceMeasure = (
    entity: any,
    measurementUnit: string,
    distanceMeasure: DistanceMeasure
  ) => {
    if (ref.current && ref.current.cesiumElement) {
      const positions = entity.polyline?.positions?.getValue();
      let groundDistanceMeasure = 0;
      for (let i = 0; i < positions.length - 1; i++) {
        const pos1 = positions[i];
        const pos2 = positions[i + 1];

        const distance = distanceMeasure?.getDistance(pos1, pos2);
        groundDistanceMeasure += distance ?? 0;
      }

      const converter = new UnitConverterHelper();
      const originalUnit = "meters";
      const result = converter.convert(
        groundDistanceMeasure,
        originalUnit,
        measurementUnit
      );

      return result;
    }
  };

  const GetSurfaceMeasure = (
    entity: any,
    measurementUnit: string,
    distanceSurfaceMeasure: DistanceSurfaceMeasure
  ) => {
    if (ref.current && ref.current.cesiumElement) {
      const positions = entity.polyline?.positions?.getValue();
      let groundDistanceMeasure = 0;
      for (let i = 0; i < positions.length - 1; i++) {
        const pos1 = positions[i];
        const pos2 = positions[i + 1];
        const distance = distanceSurfaceMeasure?.getDistance(pos1, pos2);
        groundDistanceMeasure += distance ?? 0;
      }

      const converter = new UnitConverterHelper();
      const originalUnit = "meters";
      const result = converter.convert(
        groundDistanceMeasure,
        originalUnit,
        measurementUnit
      );

      return result;
    }
  };

  const getAreaMeasure = (entity: any, measurementUnit: string) => {
    if (ref.current && ref.current.cesiumElement) {
      const area = new AreaMeasure(ref.current.cesiumElement);
      const positions = entity.polyline?.positions?.getValue();
      const areaMeasure = area.getArea(positions);

      const converter = new UnitConverterHelper();
      const originalUnit = "meters2";
      const result = converter.convert(
        areaMeasure,
        originalUnit,
        measurementUnit + "2"
      );

      return result;
    }
  };

  const onFileUpload = async (file: File) => {
    setIsUploading(true);

    const shapeFilesService = new ShapeFilesV1Service();
    try {
      const uploadResponse = await shapeFilesService.uploadShapeFile(file);

      if (uploadResponse != null) {
        const name = uploadResponse.substring(
          uploadResponse.lastIndexOf("/") + 1,
          uploadResponse.length
        );

        const newShapeFile = {
          name: name,
          displayName: file.name.substring(0, file.name.lastIndexOf(".")),
          url: uploadResponse,
          accountId: context.account.accountId,
        };

        const response = await shapeFilesService.create(newShapeFile);
        if (response.status === 201) {
          const newShapeFile = response.data;
          var newShapeFiles = [newShapeFile, ...shapeFiles];
          setShapeFiles(newShapeFiles);

          var view2DObjectResult = await view2DObjectByFile(
            file,
            newShapeFile.id
          );
          if (view2DObjectResult && view2DObjectResult.errorMessage) {
            context.setAlert({
              message: `Failed to display object!
               ${view2DObjectResult.errorMessage}`,
              theme: Themes.Danger,
            });
          }
        }
      }
    } catch (error) {
      console.error("File upload failed:", error);
      context.setAlert({
        message: "File upload failed. Please try again.",
        theme: Themes.Danger,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const onFileSelect = async (file: IShapeFile) => {
    console.log("Selected file:", file);

    var view2DObjectResult = await view2DObjectByUrl(file);
    if (view2DObjectResult && view2DObjectResult.errorMessage) {
      context.setAlert({
        message: `Failed to display object!
               ${view2DObjectResult.errorMessage}`,
        theme: Themes.Danger,
      });
    }
  };

  const view2DObjectByUrl = async (shapeFile: IShapeFile) => {
    var blob = await Utils.url2blob(shapeFile.url);
    if (blob) {
      var fileName = shapeFile.url.split("/").pop() || "";
      var files = [new File([blob], fileName)];
      const file = files[0];

      return await view2DObject(file, shapeFile.id);
    }
  };

  const view2DObjectByFile = async (file: File, id: string) => {
    return await view2DObject(file, id);
  };

  const view2DObject = async (file: File, id: string) => {
    if (!ref.current || !ref.current.cesiumElement) return;

    const viewer = ref.current.cesiumElement;

    var errorMessage;

    const fileExtension = file.name.split(".").pop()?.toLocaleLowerCase();
    if (fileExtension === "geojson") {
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        const geojson = JSON.parse(e.target.result);

        const geojsonDataSource = await GeoJsonDataSource.load(geojson, {
          clampToGround: true,
        });
        await viewer.dataSources.add(geojsonDataSource);

        var zoomedToGeoJSON = viewer.zoomTo(geojsonDataSource);
        if (!zoomedToGeoJSON) {
          errorMessage = "Unable to load GeoJSON file!";
        }
      };

      reader.readAsText(file);
    } else if (fileExtension === "zip") {
      try {
        const data = await file.arrayBuffer();
        const geojson = await shp(data);

        const shpDataSource = await GeoJsonDataSource.load(geojson, {
          clampToGround: true,
        });
        await viewer.dataSources.add(shpDataSource);

        var entities = shpDataSource.entities.values;
        for (let entity of entities) {
          entity.name = file.name;
          entity.properties = new PropertyBag({ itemId: id });
        }

        var zoomedToSHP = viewer.zoomTo(shpDataSource);
        if (!zoomedToSHP) {
          errorMessage = "Unable to load SHP file!";
        }
      } catch (error) {
        errorMessage = "Unable to load SHP file!";
      }
    } else if (fileExtension === "dxf") {
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        try {
          const parser = new dxfParser();
          const dxfData = parser.parseSync(e.target.result);
          const geoJSONData = Utils.convertDXFToGeoJSON(dxfData);
          const dxfDataSource = await GeoJsonDataSource.load(geoJSONData, {
            clampToGround: true,
          });
          await viewer.dataSources.add(dxfDataSource);

          var entities = dxfDataSource.entities.values;
          for (let entity of entities) {
            entity.name = file.name;
            entity.properties = new PropertyBag({ itemId: id });
          }

          var zoomedToDXF = viewer.zoomTo(dxfDataSource);
          if (!zoomedToDXF) {
            errorMessage = "Unable to load DXF file!";
          }
        } catch (error) {
          errorMessage = "Unable to load DXF file!";
        }
      };
      reader.readAsText(file);
    } else if (fileExtension === "kml" || fileExtension === "kmz") {
      try {
        const kmlDataSource = await KmlDataSource.load(file, {
          camera: viewer.scene.camera,
          canvas: viewer.scene.canvas,
          clampToGround: true,
        });
        await viewer.dataSources.add(kmlDataSource);
        var zoomedToKML = await viewer.zoomTo(kmlDataSource);
        if (!zoomedToKML) {
          errorMessage = "Unable to load KML file!";
        }
      } catch (error) {
        errorMessage = "Unable to load KML file!";
      }
    } else {
      errorMessage = "No valid .zip, .dxf, .kml or .kmz file!";
    }

    var newFileObj = {
      inputFile: file,
      errorMessage: errorMessage,
    };
    return newFileObj;
  };

  const handleShowLablesToggle = () => setIsShowLables(!isShowLables);

  const handleEnable3DMapToggle = async () => {
    if (ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;
      if (enable3DMap) {
        if (viewer.scene.primitives.length > 0) {
          for (let i = 0; i < viewer.scene.primitives.length; i++) {
            const primitive = viewer.scene.primitives.get(i);
            if (primitive && primitive.isCesium3DTileset) {
              viewer.scene.primitives.remove(primitive);
            }
          }
        }

        setEnable3DMap(false);
        setEnableQRCode(false);
      } else {
        const tileset = await createGooglePhotorealistic3DTileset({
          key: environment.Google_Api_Key,
        });
        viewer.scene.primitives.add(tileset);

        setEnable3DMap(true);
      }

      cancelQREvent();
    }
  };

  const addQRCodeEvent = async (e: any, target: any) => {
    if (!enableQRCode) return;

    if (ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;

      // Remove the current QR entity if it exists
      if (currentQR) {
        const entity = viewer.entities.getById(currentQR.id);
        if (entity) {
          viewer.entities.remove(entity);
        }
      }

      const cartesian = viewer.scene.pickPosition(e.position);

      if (!cartesian) {
        console.error("Click position is not on the globe or 3D tiles.");
        return;
      }

      // Convert Cartesian to Cartographic
      const cartographic = Cartographic.fromCartesian(cartesian);

      // Check if Google 3D tiles are enabled
      let adjustedHeight = cartographic.height;

      if (enable3DMap) {
        // Use the height from the 3D tiles
        adjustedHeight += 0.5;
      } else {
        // Sample the terrain height for more accurate results
        const coordinates = await sampleTerrainMostDetailed(
          viewer.terrainProvider,
          [cartographic]
        );
        adjustedHeight = coordinates[0]?.height ?? cartographic.height;
        adjustedHeight += 0.1;
      }

      const longitude = CesiumMath.toDegrees(cartographic.longitude);
      const latitude = CesiumMath.toDegrees(cartographic.latitude);

      const uuid = uuidv4();

      const QR: IQrCode = {
        id: uuid,
        latitude: latitude,
        longitude: longitude,
        altitude: adjustedHeight, // Use the adjusted height
        heading: 0,
        created: new Date(),
        description: "",
      };

      await addQRCode(QR, true);
    }
  };

  const addQRCode = async (QR: IQrCode, isNew: boolean = false) => {
    if (ref.current && ref.current.cesiumElement) {
      const entity = ref.current.cesiumElement.entities.getById(QR.id);
      if (entity) {
        return;
      }

      // Correct position for the QR code plane
      const position = Cartesian3.fromDegrees(
        QR.longitude,
        QR.latitude,
        QR.altitude // Use the correct altitude
      );

      // Align the plane to stand vertically
      const hqr = new HeadingPitchRoll(
        CesiumMath.toRadians(QR.heading), // Heading
        CesiumMath.toRadians(90), // Pitch (90 degrees to make it vertical)
        0 // Roll
      );

      // Generate the QR code content
      const content = `{https://www.foundat.com/app,"ast":0,"la":${QR.latitude},"lo":${QR.longitude},"a":${QR.altitude},"h":0}`;
      const qrCode = await QRCode.toDataURL(content);

      if (qrCode) {
        const entity = ref.current?.cesiumElement?.entities.add({
          id: QR.id,
          name: QR.id,
          position: position, // Use the correct position
          plane: {
            plane: new Plane(Cartesian3.UNIT_Y, 0), // Align the plane to stand vertically
            dimensions: new Cartesian3(QR_SIZE, QR_SIZE, QR_SIZE), // Set the size of the plane
            material: new ImageMaterialProperty({ image: qrCode }), // Apply the QR code as a texture
          },
          orientation: Transforms.headingPitchRollQuaternion(position, hqr), // Align the plane orientation
        });

        if (isNew) {
          setCurrentQR(QR);
          if (entity) ref.current?.cesiumElement?.flyTo(entity);
        }
      }
    }
  };

  const updateQREntity = (qr: IQrCode) => {
    if (ref.current && ref.current.cesiumElement) {
      const entity = ref.current.cesiumElement.entities.getById(qr.id);

      if (entity) {
        entity.position = new CallbackPositionProperty(
          () =>
            Cartesian3.fromDegrees(
              qr.longitude -
                Math.sin(CesiumMath.toRadians(qr.heading)) * QR_SPACE,
              qr.latitude -
                Math.cos(CesiumMath.toRadians(qr.heading)) * QR_SPACE,
              qr.altitude
            ),
          false
        );

        const position = Cartesian3.fromDegrees(
          qr.longitude,
          qr.latitude,
          qr.altitude
        );

        const hqr = new HeadingPitchRoll(
          CesiumMath.toRadians(qr.heading),
          0,
          0
        );

        entity.orientation = new CallbackProperty(
          () => Transforms.headingPitchRollQuaternion(position, hqr),
          false
        );
      }
    }
  };

  const handleQRCodeToggle = async () => {
    setEnableQRCode(!enableQRCode);
    setCurrentQR(null);
  };

  const updateQRLatitude = (amount: number) => {
    if (currentQR) {
      const clone = structuredClone(currentQR);
      clone.latitude += amount;
      setCurrentQR(clone);
    }
  };

  const updateQRLongitude = (amount: number) => {
    if (currentQR) {
      const clone = structuredClone(currentQR);
      clone.longitude += amount;
      setCurrentQR(clone);
    }
  };

  const updateQRAltitude = (amount: number) => {
    if (currentQR) {
      const clone = structuredClone(currentQR);
      clone.altitude += amount;
      setCurrentQR(clone);
    }
  };

  const updateQRHeading = (amount: number) => {
    if (currentQR) {
      const clone = structuredClone(currentQR);
      clone.heading += amount;
      if (clone.heading < 0) clone.heading = clone.heading + 360;
      if (clone.heading > 360) clone.heading = clone.heading - 360;
      setCurrentQR(clone);
    }
  };

  const saveQREvent = async (name: string) => {
    setQRIsSaving(true);
    try {
      if (currentQR) {
        currentQR.description = name;

        const qrCodeService = new QrCodesV1Service();
        if (allQR && allQR.length > 0) {
          const exsists = allQR.find((el) => el.id === currentQR.id);

          if (!exsists) {
            const newQRCode = await qrCodeService.create(currentQR);
            setAllQR([newQRCode.data, ...allQR]);
            setCurrentQR(null);
          } else if (!_.isEqual(exsists, currentQR)) {
            const updatedQRCode = await qrCodeService.update(
              currentQR.id,
              currentQR
            );

            setAllQR(
              allQR.map((el) =>
                el.id === currentQR.id ? updatedQRCode.data : el
              )
            );
            setCurrentQR(null);
          } else {
            setCurrentQR(null);
          }
        } else {
          const newQRCode = await qrCodeService.create(currentQR);
          setAllQR([newQRCode.data]);
          setCurrentQR(null);
        }
      }
    } catch (error) {
      console.error("Error saving QR code:", error);
      context.setAlert({
        message: "Failed to save QR code. Please try again.",
        theme: Themes.Danger,
      });
    } finally {
      setQRIsSaving(false);
    }
  };

  const cancelQREvent = () => {
    if (ref.current && ref.current.cesiumElement && currentQR) {
      const entity = ref.current.cesiumElement.entities.getById(currentQR.id);
      if (entity) {
        ref.current.cesiumElement.entities.remove(entity);
      }
      setCurrentQR(null);
    }
  };

  const selectedEntityChangeEvent = (entity: Entity) => {
    if (entity) {
      console.log(entity);
    }
  };

  return (
    <>
      <div
        className="fixed top-20 left-10 w-[300px] bg-transparent flex flex-col gap-2"
        style={{ zIndex: 10 }}
      >
        <MapSettings
          onShowLablesToggle={handleShowLablesToggle}
          onEnable3DMapToggle={handleEnable3DMapToggle}
          onQRCodeToggle={handleQRCodeToggle}
          isEnable3DMap={enable3DMap}
          isShowLables={isShowLables}
          isShowQRCode={true}
          isEnableQRCode={enableQRCode}
        />
        {!enableQRCode && (
          <div className="flex flex-col gap-2">
            <MeasurePanel
              areaMeasure={areaSurfaceMeasure}
              nonGroundDistanceMeasure={nonGroundDistance}
              groundDistanceMeasure={groundDistanceMeasurement}
              measurementEnded={measurementEnded}
              isMeasuring={isMeasuring}
              onSaveMeasurement={saveMeasurement}
              onCancelMeasurement={cancelMeasurement}
              distance={distanceValue}
            />

            <FilesList
              onFileUpload={onFileUpload}
              onFileSelect={onFileSelect}
              shapeFiles={shapeFiles}
              isUploading={isUploading}
            />
          </div>
        )}
        {enableQRCode && currentQR && (
          <QRCodePanel
            currentQR={currentQR}
            saveQREvent={saveQREvent}
            cancelQREvent={cancelQREvent}
            isSaving={isQRSaving}
            updateQRLongitude={updateQRLongitude}
            updateQRLatitude={updateQRLatitude}
            updateQRAltitude={updateQRAltitude}
            updateQRHeading={updateQRHeading}
          />
        )}
      </div>
      <div>
        <LocationBookmarkPanel viewer={ref.current?.cesiumElement} />
      </div>
      <Viewer
        ref={ref}
        animation={false}
        timeline={false}
        homeButton={true}
        sceneModePicker={false}
        baseLayerPicker={true}
        navigationHelpButton={false}
        shouldAnimate={true}
        requestRenderMode={true}
        infoBox={true}
        extend={viewerDragDropMixin}
        onClick={(e, target) => addQRCodeEvent(e, target)}
        geocoder={IonGeocodeProviderType.GOOGLE}
        onSelectedEntityChange={(entity) => selectedEntityChangeEvent(entity)}
      ></Viewer>
    </>
  );
};
